import { useState, type FormEvent } from 'react';
import { LinkIcon, SearchIcon } from 'lucide-react';
import type { AuditLogEntry, LinkAuditLogInput, SecurityIncident } from '@kennisbank/shared';
import { ApiError, apiGet, apiPost } from '../../api/client.js';
import { formatMoment } from './termijn.js';

/**
 * Reconstructie (plan §7b): audit-logregels bij de melding vastleggen, zodat
 * de feitenbasis van een AP-melding niet in een los inzagescherm blijft
 * hangen.
 *
 * Het tijdvenster staat standaard op de datums die de melder opgaf (datum van
 * het incident tot en met de ontdekkingsdatum). Dat is bijna altijd het juiste
 * venster en scheelt de behandelaar het overtypen van twee datums.
 *
 * De regelweergave is die van Beheer -> Audit log: dezelfde kolommen in
 * dezelfde volgorde, zodat wie beide schermen gebruikt niet hoeft te schakelen.
 */

interface AuditPage {
  entries: AuditLogEntry[];
  nextCursor: string | null;
}

export function LogKoppelPaneel({
  incident,
  onLinked,
}: {
  incident: SecurityIncident;
  onLinked: () => void;
}): JSX.Element {
  const [van, setVan] = useState(incident.melderOpgave.datumLek);
  const [tot, setTot] = useState(incident.melderOpgave.datumOntdekt);
  const [gebruiker, setGebruiker] = useState('');
  const [actie, setActie] = useState('');

  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);
  const [gekozen, setGekozen] = useState<Set<string>>(new Set());
  const [toelichting, setToelichting] = useState('');
  const [zoekt, setZoekt] = useState(false);
  const [koppelt, setKoppelt] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const alGekoppeld = new Set(incident.logKoppelingen.map((k) => k.auditLogId));

  async function zoek(e: FormEvent): Promise<void> {
    e.preventDefault();
    setZoekt(true);
    setErr(null);
    try {
      const page = await apiGet<AuditPage>('/api/v1/beheer/audit-log', {
        limit: 50,
        ...(van ? { from: van } : {}),
        ...(tot ? { to: tot } : {}),
        ...(gebruiker.trim() ? { userId: gebruiker.trim() } : {}),
        ...(actie.trim() ? { action: actie.trim() } : {}),
      });
      setEntries(page.entries);
      setGekozen(new Set());
    } catch (e2) {
      setErr(
        e2 instanceof ApiError
          ? `Zoeken mislukt (${e2.status}): ${e2.message}`
          : 'Zoeken in de audit log mislukt.',
      );
    } finally {
      setZoekt(false);
    }
  }

  async function koppel(): Promise<void> {
    if (gekozen.size === 0) return;
    setKoppelt(true);
    setErr(null);
    const body: LinkAuditLogInput = {
      auditLogIds: [...gekozen],
      ...(toelichting.trim() ? { toelichting: toelichting.trim() } : {}),
    };
    try {
      await apiPost(`/api/v1/beheer/incidents/${incident.id}/log`, body);
      setGekozen(new Set());
      setToelichting('');
      onLinked();
    } catch (e2) {
      setErr(
        e2 instanceof ApiError
          ? `Koppelen mislukt (${e2.status}): ${e2.message}`
          : 'Koppelen mislukt.',
      );
    } finally {
      setKoppelt(false);
    }
  }

  function toggle(id: string): void {
    setGekozen((prev) => {
      const volgende = new Set(prev);
      if (volgende.has(id)) volgende.delete(id);
      // Het schema koppelt maximaal 25 regels per keer.
      else if (volgende.size < 25) volgende.add(id);
      return volgende;
    });
  }

  const veldClass =
    'rounded-md border border-ring2 bg-white px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-900/40';

  return (
    <section className="rounded-2xl border border-ring2 bg-white p-4 shadow-card">
      <h2 className="text-base font-bold text-ink-900">Logregels koppelen</h2>
      <p className="mt-1 text-sm text-ink-900/65">
        Zoek in de audit log rond de opgegeven data en leg de regels vast die bij deze melding
        horen. Gekoppelde regels verschijnen in de tijdlijn hierboven.
      </p>

      <form onSubmit={(e) => void zoek(e)} className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-900/70">
          Van
          <input
            type="date"
            value={van}
            onChange={(e) => setVan(e.target.value)}
            className={veldClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-900/70">
          Tot en met
          <input
            type="date"
            value={tot}
            onChange={(e) => setTot(e.target.value)}
            className={veldClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-900/70">
          Gebruiker (id)
          <input
            type="text"
            value={gebruiker}
            onChange={(e) => setGebruiker(e.target.value)}
            placeholder="optioneel"
            className={veldClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-900/70">
          Actie (prefix)
          <input
            type="text"
            value={actie}
            onChange={(e) => setActie(e.target.value)}
            placeholder="bijv. document. of login."
            className={veldClass}
          />
        </label>
        <button
          type="submit"
          disabled={zoekt}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <SearchIcon size={14} /> {zoekt ? 'Zoeken…' : 'Zoeken'}
        </button>
      </form>

      {err && <p className="mt-3 rounded-md bg-error/10 px-3 py-2 text-sm text-error">{err}</p>}

      {entries === null ? (
        <p className="mt-4 rounded-md bg-ink-50 px-3 py-3 text-sm text-ink-900/60">
          Het venster staat op de data uit de melding ({incident.melderOpgave.datumLek} tot en met{' '}
          {incident.melderOpgave.datumOntdekt}). Klik op Zoeken om te zien welke handelingen in die
          periode zijn vastgelegd.
        </p>
      ) : entries.length === 0 ? (
        <p className="mt-4 rounded-md bg-ink-50 px-3 py-3 text-sm text-ink-900/60">
          Geen logregels in dit tijdvenster. Niet elk incident laat sporen na in de app: een
          verkeerd geadresseerde mail of een verloren dossier staat nergens in de audit log.
        </p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto rounded-xl border border-ring2">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-ring2 text-[12px] uppercase tracking-wide text-ink-900/55">
                  <th className="w-10 px-3 py-2.5 font-semibold">
                    <span className="sr-only">Koppelen</span>
                  </th>
                  <th className="px-4 py-2.5 font-semibold">Tijdstip</th>
                  <th className="px-4 py-2.5 font-semibold">Gebruiker</th>
                  <th className="px-4 py-2.5 font-semibold">Actie</th>
                  <th className="px-4 py-2.5 font-semibold">Object</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const gekoppeld = alGekoppeld.has(e.id);
                  return (
                    <tr key={e.id} className="border-b border-ring2/60 align-top last:border-0">
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={gekoppeld || gekozen.has(e.id)}
                          disabled={gekoppeld}
                          onChange={() => toggle(e.id)}
                          aria-label={`Koppel regel ${e.action} van ${formatMoment(e.at)}`}
                          className="h-4 w-4 accent-brand-600 disabled:opacity-50"
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-ink-900/70 tabular-nums">
                        {formatMoment(e.at)}
                      </td>
                      <td className="px-4 py-2.5 text-ink-900/80">
                        {e.actorUserId ?? <span className="text-ink-900/40">systeem</span>}
                        {e.actorRole && (
                          <span className="ml-1.5 text-[11px] text-ink-900/45">
                            ({e.actorRole})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="rounded-sm bg-brand-50 px-2 py-0.5 font-medium text-brand-700">
                          {e.action}
                        </span>
                        {gekoppeld && (
                          <span className="ml-1.5 text-[11px] text-ink-900/45">al gekoppeld</span>
                        )}
                      </td>
                      <td
                        className="max-w-xs truncate px-4 py-2.5 text-ink-900/60"
                        title={e.targetPath}
                      >
                        {e.targetPath ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-[12px] font-medium text-ink-900/70">
              Toelichting bij deze koppeling (optioneel)
              <input
                type="text"
                value={toelichting}
                onChange={(e) => setToelichting(e.target.value)}
                placeholder="Bijvoorbeeld: downloads vlak voor de uitdiensttreding."
                className={veldClass}
              />
            </label>
            <button
              type="button"
              disabled={koppelt || gekozen.size === 0}
              onClick={() => void koppel()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              <LinkIcon size={14} />
              {koppelt
                ? 'Koppelen…'
                : gekozen.size === 0
                  ? 'Koppel geselecteerde regels'
                  : `Koppel ${gekozen.size} ${gekozen.size === 1 ? 'regel' : 'regels'}`}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
